// ADIRATH website — Contact page. Split: form + office info, quick cards, WhatsApp CTA.
import React from 'react';
import { Button } from './components/buttons/Button';
import { Card } from './components/data-display/Card';
import { Input } from './components/forms/Input';
import { Select } from './components/forms/Select';
import { Switch } from './components/forms/Switch';
import { Eyebrow } from './primitives';
import { Icon } from './Icon';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\d{7,15}$/;

const COUNTRY_CODES = [
  { code: '+91',  iso: 'IN' },
  { code: '+1',   iso: 'US' },
  { code: '+44',  iso: 'GB' },
  { code: '+971', iso: 'AE' },
  { code: '+966', iso: 'SA' },
  { code: '+65',  iso: 'SG' },
  { code: '+60',  iso: 'MY' },
  { code: '+61',  iso: 'AU' },
  { code: '+49',  iso: 'DE' },
  { code: '+33',  iso: 'FR' },
  { code: '+86',  iso: 'CN' },
  { code: '+81',  iso: 'JP' },
];
const flagUrl = (iso) => `https://flagcdn.com/w20/${iso.toLowerCase()}.png`;

function CountryDropdown({ value, onChange, hasError }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const selected = COUNTRY_CODES.find((c) => c.code === value) || COUNTRY_CODES[0];

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const borderColor = hasError ? 'var(--error, #e53935)' : open ? 'var(--accent)' : 'var(--border-strong)';
  const boxShadow   = open && !hasError ? '0 0 0 4px var(--ring)' : 'none';

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0, width: 120 }}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', height: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 10px', gap: 6,
          border: `1.5px solid ${borderColor}`, borderRadius: 'var(--radius-md)',
          background: 'var(--surface-card)', cursor: 'pointer', outline: 'none',
          fontFamily: 'var(--font-ui)', fontSize: 14, fontWeight: 600, color: 'var(--text-strong)',
          boxShadow, transition: 'border-color 150ms, box-shadow 150ms',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <img src={flagUrl(selected.iso)} alt={selected.iso} width={20} height={15} style={{ borderRadius: 2, objectFit: 'cover', flexShrink: 0 }} />
          <span style={{ fontSize: 13 }}>{selected.iso} {selected.code}</span>
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, opacity: 0.5, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Dropdown list */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: 'var(--surface-card)', border: '1.5px solid var(--border-strong)',
          borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
          minWidth: 160, maxHeight: 240, overflowY: 'auto',
          padding: '4px 0',
        }}>
          {COUNTRY_CODES.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => { onChange(c.code); setOpen(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', border: 'none', background: c.code === value ? 'var(--accent-soft)' : 'transparent',
                cursor: 'pointer', fontFamily: 'var(--font-ui)', fontSize: 13.5,
                color: 'var(--text-strong)', textAlign: 'left',
              }}
              onMouseEnter={(e) => { if (c.code !== value) e.currentTarget.style.background = 'var(--surface-hover, rgba(0,0,0,0.04))'; }}
              onMouseLeave={(e) => { if (c.code !== value) e.currentTarget.style.background = 'transparent'; }}
            >
              <img src={flagUrl(c.iso)} alt={c.iso} width={22} height={16} style={{ borderRadius: 2, objectFit: 'cover', flexShrink: 0 }} />
              <span style={{ fontWeight: 600 }}>{c.iso}</span>
              <span style={{ color: 'var(--text-muted)' }}>{c.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, fontSize: 12.5, color: 'var(--error, #e53935)', fontFamily: 'var(--font-ui)' }}>
      <Icon name="alert-circle" style={{ width: 13, height: 13, flexShrink: 0 }} />{msg}
    </span>
  );
}

const RECAPTCHA_SITE_KEY = '6Lf9D2ktAAAAAO7ll-XXQcoErWqkaU6kvTyHNmpu';

export function ContactPage({ onNavigate }) {

  const [form, setForm] = React.useState({ name: '', company: '', email: '', countryCode: '+91', phone: '', service: '', message: '' });
  const [errors, setErrors] = React.useState({});
  const [sent, setSent] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const [updates, setUpdates] = React.useState(true);

  // Load reCAPTCHA v3 script
  React.useEffect(() => {
    if (document.getElementById('recaptcha-script')) return;
    const script = document.createElement('script');
    script.id  = 'recaptcha-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);
  }, []);
  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: '' }));
  };
  const setPhone = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 15);
    setForm((f) => ({ ...f, phone: digits }));
    if (errors.phone) setErrors((er) => ({ ...er, phone: '' }));
  };

  function validate() {
    const e = {};
    if (!form.name.trim())                      e.name    = 'Name is required.';
    if (!form.email.trim())                     e.email   = 'Email is required.';
    else if (!EMAIL_RE.test(form.email.trim())) e.email   = 'Enter a valid email address.';
    if (!form.phone.trim())                     e.phone   = 'Phone is required.';
    else if (!PHONE_RE.test(form.phone.trim())) e.phone   = 'Enter a valid phone number.';
    if (!form.message.trim())                   e.message = 'Please describe your requirement.';
    return e;
  }

  async function handleSubmit() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSending(true);
    setSubmitError('');
    try {
      // Get reCAPTCHA v3 token
      const token = await new Promise((resolve, reject) => {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(RECAPTCHA_SITE_KEY, { action: 'contact' })
            .then(resolve)
            .catch(reject);
        });
      });

      const res = await fetch('/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          phone: `${form.countryCode} ${form.phone}`,
          subscribeUpdates: updates,
          recaptchaToken: token,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSent(true);
      } else {
        setSubmitError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('[Contact form]', err);
      // Distinguish reCAPTCHA load failure from network/server errors
      if (!window.grecaptcha || err?.message?.includes('grecaptcha')) {
        setSubmitError('Security check failed to load. Please refresh the page and try again.');
      } else {
        setSubmitError('Could not reach the server. Please try again or email us at info@eeepvtltd.com.');
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <div>
      <section data-screen-label="Contact Hero" style={{ background: 'var(--grad-hero)', color: '#fff', padding: 'calc(var(--space-9) + 40px) 0 var(--space-8)', position: 'relative', overflow: 'hidden' }}>
        <div className="adr-blob" style={{ position: 'absolute', width: 460, height: 460, left: -140, top: -120, background: 'var(--blue-400)', borderRadius: '50%', filter: 'blur(100px)', opacity: 0.34 }} />
        <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)', position: 'relative' }}>
          <Eyebrow dark>Contact Us</Eyebrow>
          <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--fs-display-2)', letterSpacing: '-0.03em', lineHeight: 1.05, margin: '0 0 16px', maxWidth: 760, color: '#fff' }}>
            Let's engineer your clean-energy solution.
          </h1>
          <p style={{ fontSize: 'var(--fs-lead)', color: 'var(--text-on-dark-muted)', maxWidth: 560 }}>
            Tell us about your project. Our engineers respond within one business day.
          </p>
        </div>
      </section>

      <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: 'var(--space-8) var(--gutter)' }}>
        <div className="adr-contact-grid" style={{ display: 'grid', gap: 40, alignItems: 'start' }}>

          <Card variant="solid" padding="clamp(1.75rem, 3vw, 2.75rem)" style={{ boxShadow: 'var(--shadow-lg)' }}>
            {!sent ? (
              <React.Fragment>
                <h2 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 'var(--fs-h3)', margin: '0 0 6px', color: 'var(--text-strong)' }}>Request a Consultation</h2>
                <p style={{ margin: '0 0 26px', color: 'var(--text-muted)', fontSize: 15 }}>Fields marked * are required.</p>
                <div className="adr-contact-fields" style={{ display: 'grid', gap: 18 }}>

                  <div>
                    <Input label="Name" required icon={<Icon name="user" style={{ width: 18, height: 18 }} />} placeholder="Your name" value={form.name} onChange={set('name')}
                      style={errors.name ? { borderColor: 'var(--error, #e53935)' } : {}} />
                    <FieldError msg={errors.name} />
                  </div>

                  <div>
                    <Input label="Company" icon={<Icon name="building-2" style={{ width: 18, height: 18 }} />} placeholder="Company" value={form.company} onChange={set('company')} />
                  </div>

                  <div>
                    <Input label="Email" type="email" required icon={<Icon name="mail" style={{ width: 18, height: 18 }} />} placeholder="you@company.com" value={form.email} onChange={set('email')}
                      style={errors.email ? { borderColor: 'var(--error, #e53935)' } : {}} />
                    <FieldError msg={errors.email} />
                  </div>

                  <div>
                    <span style={{ display: 'block', marginBottom: 8, fontFamily: 'var(--font-ui)', fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-strong)' }}>
                      Phone <span style={{ color: 'var(--error, #e53935)' }}>*</span>
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <CountryDropdown
                        value={form.countryCode}
                        onChange={(code) => setForm((f) => ({ ...f, countryCode: code }))}
                        hasError={!!errors.phone}
                      />
                      <input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={15}
                        placeholder="Phone number"
                        value={form.phone}
                        onChange={setPhone}
                        style={{
                          flex: 1, minWidth: 0, padding: '0 14px', height: 44,
                          border: `1.5px solid ${errors.phone ? 'var(--error, #e53935)' : 'var(--border-strong)'}`,
                          borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)',
                          fontSize: 'var(--fs-body)', color: 'var(--text-strong)', outline: 'none',
                          background: 'var(--surface-card)',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = '0 0 0 4px var(--ring)'; }}
                        onBlur={(e) => { e.target.style.borderColor = errors.phone ? 'var(--error, #e53935)' : 'var(--border-strong)'; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>
                    <FieldError msg={errors.phone} />
                  </div>

                  <div className="adr-col-span-2">
                    <Select label="Service Required" value={form.service} onChange={set('service')}
                      options={['Battery Energy Storage (BESS)', 'Industrial Mobility Batteries', 'Robotics System Integration', 'Industrial Automation', 'Solar EPC Solutions', 'New Product Development', 'Others']} />
                  </div>

                  <div className="adr-col-span-2">
                    <label style={{ display: 'block' }}>
                      <span style={{ display: 'block', marginBottom: 8, fontFamily: 'var(--font-ui)', fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-strong)' }}>
                        Message <span style={{ color: 'var(--error, #e53935)' }}>*</span>
                      </span>
                      <textarea rows={4} value={form.message} onChange={set('message')} placeholder="Tell us about your requirement…"
                        style={{
                          width: '100%', resize: 'vertical', border: `1.5px solid ${errors.message ? 'var(--error, #e53935)' : 'var(--border-strong)'}`,
                          borderRadius: 'var(--radius-md)', padding: '12px 14px', fontFamily: 'var(--font-body)',
                          fontSize: 'var(--fs-body)', color: 'var(--text-strong)', outline: 'none', boxSizing: 'border-box',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = '0 0 0 4px var(--ring)'; }}
                        onBlur={(e) => { e.target.style.borderColor = errors.message ? 'var(--error, #e53935)' : 'var(--border-strong)'; e.target.style.boxShadow = 'none'; }} />
                    </label>
                    <FieldError msg={errors.message} />
                  </div>

                </div>

                {Object.keys(errors).length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18, padding: '12px 16px', borderRadius: 'var(--radius-md)', background: 'rgba(229,57,53,0.08)', border: '1px solid rgba(229,57,53,0.25)', color: '#c62828', fontSize: 13.5 }}>
                    <Icon name="alert-triangle" style={{ width: 18, height: 18, flexShrink: 0 }} />
                    Please fix the errors above before submitting.
                  </div>
                )}

                {submitError && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18, padding: '12px 16px', borderRadius: 'var(--radius-md)', background: 'rgba(229,57,53,0.08)', border: '1px solid rgba(229,57,53,0.25)', color: '#c62828', fontSize: 13.5 }}>
                    <Icon name="alert-triangle" style={{ width: 18, height: 18, flexShrink: 0 }} />
                    {submitError}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginTop: 24 }}>
                  <Switch checked={updates} label="Send me ADIRATH updates" onChange={setUpdates} />
                  <Button variant="primary" size="lg" iconRight={<Icon name={sending ? 'loader' : 'send'} style={{ width: 18, height: 18 }} />} onClick={handleSubmit} disabled={sending}>
                    {sending ? 'Sending…' : 'Submit Request'}
                  </Button>
                </div>
              </React.Fragment>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 10px' }}>
                <span style={{ display: 'inline-flex', width: 72, height: 72, borderRadius: '50%', background: 'var(--energy-soft)', color: 'var(--success)', alignItems: 'center', justifyContent: 'center', marginBottom: 22 }}>
                  <Icon name="check" style={{ width: 36, height: 36 }} />
                </span>
                <h2 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 'var(--fs-h3)', margin: '0 0 10px', color: 'var(--text-strong)' }}>Thank you — request received.</h2>
                <p style={{ color: 'var(--text-muted)', maxWidth: 380, margin: '0 auto 12px' }}>An ADIRATH engineer will reach out within one business day.</p>
                {updates && <p style={{ color: 'var(--text-muted)', fontSize: 13, maxWidth: 380, margin: '0 auto 24px' }}>You'll also receive ADIRATH product updates at <strong>{form.email}</strong>.</p>}
                {!updates && <p style={{ color: 'var(--text-muted)', fontSize: 13, maxWidth: 380, margin: '0 auto 24px' }}>You opted out of product updates.</p>}
                <Button variant="outline" onClick={() => { setSent(false); setSubmitError(''); setForm({ name: '', company: '', email: '', countryCode: '+91', phone: '', service: '', message: '' }); setErrors({}); setUpdates(true); }}>Submit another</Button>
              </div>
            )}
          </Card>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <Card variant="dark" padding="26px 28px">
              <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 'var(--fs-h4)', margin: '0 0 18px', color: '#fff' }}>Office Information</h3>
              {[
                ['map-pin', 'Address', [['#1/329-A, NH-44, Bengaluru Highway, Polupalli, Billanakuppam, Krishnagiri - 635115, Tamilnadu', 'https://maps.app.goo.gl/e943iKybq22gP9gu5']], 'address'],
                ['phone', 'Phone', [['04343-294648', 'tel:04343294648'], ['+91 6385 518 611', 'tel:+916385518611'], ['+91 8807 487 020', 'tel:+918807487020']], 'phone'],
                ['mail', 'Email', [['info@eeepvtltd.com', 'mailto:info@eeepvtltd.com'], ['sales@eeepvtltd.com', 'mailto:sales@eeepvtltd.com']], 'email'],
                ['clock', 'Business Hours', 'Mon–Sat · 9:00–18:00 IST', null],
              ].map(([ic, l, v, type]) => (
                <div key={l} style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
                  <span style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.08)', border: '1px solid var(--border-on-dark)', color: 'var(--energy-bright)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name={ic} style={{ width: 18, height: 18 }} />
                  </span>
                  <div>
                    <div style={{ fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-on-dark-muted)', marginBottom: 3 }}>{l}</div>
                    <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 600, color: '#fff', fontSize: 15, display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {Array.isArray(v)
                        ? v.map(([text, href]) => (
                            <a key={href} href={href}
                              {...(href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                              style={{ color: 'var(--energy-bright)', textDecoration: 'none', ...(type !== 'address' ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : { wordBreak: 'break-word' }) }}
                              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}>{text}</a>
                          ))
                        : v}
                    </div>
                  </div>
                </div>
              ))}
            </Card>

            <div style={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden', height: 180, border: '1px solid var(--border-subtle)' }}>
              <iframe
                title="ADIRATH Location"
                src="https://maps.google.com/maps?q=1/329-A,+NH-44,+Bengaluru+Highway,+Polupalli,+Billanakuppam,+Krishnagiri+635115&output=embed"
                width="100%" height="100%" style={{ border: 0, display: 'block' }}
                allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>

            <a href="https://wa.me/918807487020" target="_blank" rel="noopener noreferrer" style={{
              display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px',
              borderRadius: 'var(--radius-lg)', background: 'var(--energy)', color: 'var(--blue-900)',
              boxShadow: 'var(--glow-soft-green)', transition: 'transform var(--dur-fast)',
            }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}>
              <Icon name="message-circle" style={{ width: 26, height: 26 }} />
              <div>
                <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 15 }}>Chat on WhatsApp</div>
                <div style={{ fontSize: 13, opacity: 0.8 }}>+91 88074 87020 · Quick response, business hours</div>
              </div>
              <Icon name="arrow-up-right" style={{ width: 20, height: 20, marginLeft: 'auto' }} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
